import { useCallback, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  BeforeInstallPromptEvent,
  InstallPlatform,
  isIosSafari,
  isStandaloneDisplay,
} from "@/lib/pwaInstall";

const DISMISS_KEY = "pwa-install-dismissed";
const INSTALLED_KEY = "pwa-installed-tracked";
const SHOWN_KEY = "pwa-install-prompt-shown";

const memory = { installed: false, shown: false };

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/** Returns true the first time this signal should be sent. */
function claimOnce(kind: "installed" | "shown", storage: Storage, key: string): boolean {
  if (memory[kind]) return false;
  try {
    if (storage.getItem(key)) {
      memory[kind] = true;
      return false;
    }
    storage.setItem(key, "1");
  } catch {
    // Private mode can reject storage; the in-memory flag still dedupes this load.
  }
  memory[kind] = true;
  return true;
}

/**
 * Install banner state plus one-time PWA analytics.
 * Does not change auth, billing, or navigation.
 */
export function usePwaInstall() {
  const posthog = usePostHog();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [standalone, setStandalone] = useState(isStandaloneDisplay);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!posthog || !isStandaloneDisplay()) return;
    if (!claimOnce("installed", localStorage, INSTALLED_KEY)) return;
    posthog.capture("pwa_installed", { display_mode: "standalone" });
  }, [posthog]);

  useEffect(() => {
    const onInstalled = () => {
      setStandalone(true);
      setDeferred(null);
      if (!posthog) return;
      if (!claimOnce("installed", localStorage, INSTALLED_KEY)) return;
      posthog.capture("pwa_installed", { display_mode: "standalone" });
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, [posthog]);

  useEffect(() => {
    if (dismissed || standalone) return;

    const sync = () => {
      const prompt = window.__tfDeferredInstall;
      if (prompt) setDeferred(prompt);
    };

    sync();
    window.addEventListener("tf-install-available", sync);
    return () => window.removeEventListener("tf-install-available", sync);
  }, [dismissed, standalone]);

  const platform: InstallPlatform | null = standalone || dismissed
    ? null
    : deferred
      ? "android"
      : isIosSafari()
        ? "ios"
        : null;

  useEffect(() => {
    if (!posthog || !platform) return;
    try {
      if (!claimOnce("shown", sessionStorage, SHOWN_KEY)) return;
    } catch {
      if (memory.shown) return;
      memory.shown = true;
    }
    posthog.capture("install_prompt_shown", { platform });
  }, [posthog, platform]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Still hide for this visit.
    }
    window.__tfDeferredInstall = null;
    setDismissed(true);
    setDeferred(null);
  }, []);

  const install = useCallback(async () => {
    const promptEvent = deferred;
    if (!promptEvent) return;
    window.__tfDeferredInstall = null;
    setDeferred(null);
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice.outcome === "accepted") {
        posthog?.capture("install_prompt_accepted", { platform: "android" });
      }
    } catch {
      // The browser rejected the prompt; the banner is already hidden.
    }
  }, [deferred, posthog]);

  return { platform, dismiss, install };
}
